import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { format } from 'date-fns'
import { Suspense, useMemo } from 'react'
import { InvoiceStatusBadge } from '@/components/sv/invoice-status-badge'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { PreviewInvoiceSheetFallback } from '@/routes/billing/historical-invoices/preview-invoice-sheet/fallback'
import type { CompanyInvoicesQuery } from '@/routes/billing/historical-invoices/index.types.gen'

type Invoice = NonNullable<
  NonNullable<CompanyInvoicesQuery['company']>['invoices']
>[number]
type Row = NonNullable<Invoice['items']>[number]

type Props = {
  invoice?: Invoice | null
  isOpen: boolean
  onClose: () => void
}

export const Content = ({ invoice }: Props) => {
  const total = invoice?.total ?? 0
  const tax = invoice?.tax ?? 0
  const taxPercent = invoice?.taxPercent ?? 0
  const credits = invoice?.creditsApplied ?? 0
  const discountAmount = invoice?.discountAmount ?? 0
  const discountName = invoice?.discountName ?? ''
  const lines = invoice?.items ?? []

  const finalPrice = total + tax + credits

  let appliedDiscount = discountAmount
  if (finalPrice < discountAmount) {
    appliedDiscount = finalPrice
  }

  const grandTotal = (finalPrice - appliedDiscount) / 100

  const columns: ColumnDef<Row>[] = useMemo(
    () => [
      {
        accessorKey: 'description',
        header: 'Invoice item',
        cell: ({ row }) => (
          <div className="truncate" title={row.original?.name ?? ''}>
            {row.original?.name}
          </div>
        ),
      },
      {
        accessorKey: 'amount',
        header: () => <div className="text-right">Amount</div>,
        cell: ({ row }) => {
          const total = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
          }).format((row.original?.amount ?? 0) / 100)
          return <div className="text-right">{total}</div>
        },
      },
    ],
    []
  )

  const table = useReactTable({
    data: lines,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  if (!invoice) {
    return null
  }

  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-2">
          <div>Invoice preview</div>
          <InvoiceStatusBadge invoice={invoice} />
        </SheetTitle>
        <SheetDescription asChild>
          <div>
            <div>
              Invoice number:{' '}
              {invoice?.invoiceNumber ? invoice?.invoiceNumber : 'N/A'}
            </div>
            {invoice.invoiceDate ? (
              <div>
                Payment date:{' '}
                {format(new Date(invoice.invoiceDate), 'MMM d, yyyy')}
              </div>
            ) : null}
            {invoice.dueDate ? (
              <div>
                Due date: {format(new Date(invoice.dueDate), 'MMM d, yyyy')}
              </div>
            ) : null}
          </div>
        </SheetDescription>
      </SheetHeader>
      <div className="px-4 overflow-y-auto w-full pb-2">
        <div className="space-y-6">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow
                    key={headerGroup.id}
                    className="bg-muted/50 hover:bg-muted/50"
                  >
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length
                  ? table.getRowModel().rows.map((row) => (
                      <TableRow key={row.id}>
                        {row.getVisibleCells().map((cell) => (
                          <TableCell
                            key={cell.id}
                            style={{ maxWidth: cell.column.getSize() }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  : null}
                {!table.getRowModel().rows?.length ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      No line items found.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </div>
          <div className="flex flex-col items-end gap-2 text-sm">
            <div className="flex w-full justify-between">
              <span>Total</span>
              <span className="tabular-nums">
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                }).format(total / 100)}
              </span>
            </div>
            <div className="flex w-full justify-between">
              <span>Tax ({taxPercent}%)</span>
              <span className="tabular-nums">
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                }).format(tax / 100)}
              </span>
            </div>
            {credits ? (
              <div className="flex w-full justify-between">
                <span>Account Balance used</span>
                <span className="tabular-nums">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  }).format(credits / 100)}
                </span>
              </div>
            ) : null}
            {discountAmount ? (
              <div className="flex w-full justify-between">
                <span>Discount{discountName ? ` ${discountName}` : ''}</span>
                <span className="tabular-nums">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  }).format((appliedDiscount * -1) / 100)}
                </span>
              </div>
            ) : null}
            <div className="flex w-full justify-between border-t pt-2 font-medium">
              <span>Grand total</span>
              <span className="tabular-nums">
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                }).format(grandTotal)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export const PreviewInvoiceSheet = ({ invoice, isOpen, onClose }: Props) => {
  return (
    <Sheet open={isOpen} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="sm:max-w-[800px]" onEscapeKeyDown={onClose}>
        <Suspense fallback={<PreviewInvoiceSheetFallback />}>
          <Content invoice={invoice} isOpen={isOpen} onClose={onClose} />
        </Suspense>
      </SheetContent>
    </Sheet>
  )
}
